import {
  CmsContentAboutVisionSchema,
  type CmsContentAboutVisionForm,
} from "@/modules/cms/domain/contracts";
import { uploadImageToKey } from "@/shared/lib/upload-image-to-key";
import { ImageKeyUploadField } from "@/shared/ui/ImageKeyUploadField";
import {
  Button,
  FormCheckbox,
  FormInput,
  FormRoot,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useCallback, useState } from "react";
import type { Resolver, UseFormReturn } from "react-hook-form";

const IMAGE_KEY_PREFIX = "uploads/cms/about-vision";

type AboutVisionFormProps = {
  content: CmsContentAboutVisionForm | null;
  onSubmit: (content: CmsContentAboutVisionForm) => void;
  onCancel: () => void;
  submitting?: boolean;
};

const defaultContent: CmsContentAboutVisionForm = {
  imageKey: "",
  title: {},
  content: {},
  isActive: true,
};

export function AboutVisionForm({
  content,
  onSubmit,
  onCancel,
  submitting,
}: AboutVisionFormProps) {
  const methods = RHF.useForm<CmsContentAboutVisionForm>({
    resolver: zodResolver(
      CmsContentAboutVisionSchema,
    ) as Resolver<CmsContentAboutVisionForm>,
    defaultValues: content ?? defaultContent,
  });
  const watchImageKey = methods.watch("imageKey") ?? "";
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleSubmit = useCallback(
    async (vals: CmsContentAboutVisionForm) => {
      const data = { ...vals };
      if (pendingFile) {
        setUploading(true);
        try {
          data.imageKey = await uploadImageToKey(IMAGE_KEY_PREFIX, pendingFile);
        } finally {
          setUploading(false);
        }
      }
      onSubmit(data);
    },
    [pendingFile, onSubmit],
  );

  return (
    <FormRoot<CmsContentAboutVisionForm>
      methods={methods as UseFormReturn<CmsContentAboutVisionForm>}
      onSubmit={handleSubmit}
    >
      <div className="space-y-6">
        <ImageKeyUploadField
          keyPrefix={IMAGE_KEY_PREFIX}
          label="ຮູບ (16:9)"
          aspectHint="ອັດສ່ວນ 16:9"
          aspectRatio="aspect-video"
          widthPx={1280}
          heightPx={720}
          value={watchImageKey}
          onChange={(key) =>
            methods.setValue("imageKey", key, { shouldDirty: true })
          }
          disabled={submitting}
          deferUpload
          pendingFile={pendingFile}
          onFileSelect={setPendingFile}
          onClear={() => {
            methods.setValue("imageKey", "", { shouldDirty: true });
            setPendingFile(null);
          }}
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormInput name="title.lo" label="ຫົວຂໍ້ (ລາວ)" />
          <FormInput name="title.th" label="ຫົວຂໍ້ (ໄທ)" />
          <FormInput name="title.en" label="ຫົວຂໍ້ (EN)" />
        </div>
        <div className="space-y-2">
          <span className="font-medium text-sm">ເນື້ອຫາ (HTML)</span>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <FormTextarea
              name="content.lo"
              label="ລາວ"
              rows={4}
              className="font-mono text-sm"
            />
            <FormTextarea
              name="content.th"
              label="ໄທ"
              rows={4}
              className="font-mono text-sm"
            />
            <FormTextarea
              name="content.en"
              label="EN"
              rows={4}
              className="font-mono text-sm"
            />
          </div>
        </div>
        <FormCheckbox name="isActive" label="ເປີດໃຊ້ງານ" />
        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={submitting || uploading}>
            {uploading ? "ກຳລັງອັບໂຫຼດ..." : submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
